import classNames from "classnames/bind";
import Image from "common/blocks/Image";
import useModalContext from "common/providers/modal/useModalContext";
import SmartLink from "common/wrapper/SmartLink";
import ShortlistButton from "features/favourites/components/ShortlistButton";
import { DEFAULT_IMAGE_URL } from "lib/constants";
import { ResolvedVehicleCardSettings } from "resolvers/data/vehicleCardSettingsResolver";
import { ResolvedVehicleDisplaySettings } from "resolvers/pages/pdpPageResolver";
import { getFinanceExampleSidePanel } from "services/motorpoint/financeExampleSidePanel";
import type { ResponseVehicle } from "services/motorpoint/shortlist";
import { vehicleImageOverlayService } from "services/vehicle/vehicleImageOverlayService";
import formatCurrency from "utils/formatCurrency";

import FinanceExampleSidePanel from "../VehicleAdCard/FinanceExampleSidePanel";
import FinanceExampleSidePanelError from "../VehicleAdCard/FinanceExampleSidePanel/error";
import FinanceExampleSidePanelFooter from "../VehicleAdCard/FinanceExampleSidePanel/footer";
import styles from "./VehicleCardV2.module.scss";

const cx = classNames.bind(styles);

type VehicleCardV2Props = {
    vehicle: ResponseVehicle;
    linkTarget?: string;
    isSaved: boolean;
    vehicleCardSettings?: ResolvedVehicleCardSettings;
    vehicleDisplaySettings: ResolvedVehicleDisplaySettings;
};

const VehicleCardV2 = ({
    vehicle,
    linkTarget,
    isSaved,
    vehicleCardSettings,
    vehicleDisplaySettings,
}: VehicleCardV2Props) => {
    const { useSidebar } = useModalContext();
    const { openSidebar } = useSidebar();
    const { getImageOverlayForResponseVehicle, getCustomOverlayForResponseVehicle } =
        vehicleImageOverlayService();

    const campaignImageOverlay = getImageOverlayForResponseVehicle(
        vehicle,
        vehicleCardSettings?.marketingCampaigns ?? null
    );

    const campaignCustomOverlay = getCustomOverlayForResponseVehicle(
        vehicle,
        vehicleCardSettings?.marketingCampaigns ?? null
    );

    const hasCampaign = !!campaignCustomOverlay || !!campaignImageOverlay;

    const isAdvertisedStock = !!vehicle.Location;
    const discountedPrice =
        (vehicle.CurrentPrice ?? 0) + (!vehicle.IsCommercial ? vehicle.VatAmount || 0 : 0);
    const previousPrice =
        vehicle.ShowDiscount &&
        vehicle.PreviousPrice &&
        vehicle.PreviousPrice - discountedPrice > vehicleDisplaySettings.discountSavingThreshold
            ? vehicle.PreviousPrice
            : null;

    const vehicleImageryOrdered = vehicle?.FirstImage?.SizedImages?.sort((a, b) =>
        b.Format.localeCompare(a.Format)
    );

    const basicPrice = vehicle?.CurrentPrice ?? 0;
    const VATAmount = vehicle?.VatAmount ?? 0;

    const price = basicPrice + VATAmount;
    const originalRetailPriceBasic = vehicle?.OriginalRetailPrice?.BasicPrice ?? 0;
    const originalRetailPriceVat = vehicle?.OriginalRetailPrice?.VATAmount ?? 0;
    const originalRetailPrice = vehicle.IsCommercial
        ? originalRetailPriceBasic
        : originalRetailPriceBasic + originalRetailPriceVat;
    const savingsAgainstOriginalRetailPrice = vehicle.IsCommercial
        ? originalRetailPrice - basicPrice
        : originalRetailPrice - price;

    const showListPriceSaving = vehicleDisplaySettings.showListPriceSavingOnVehicleTile;
    const listPriceSavingThreshold = vehicleDisplaySettings.listPriceSavingThreshold;
    const listPriceSavingRegistrationYearCriteria =
        vehicleDisplaySettings.listPriceSavingRegistrationYearCriteria.split(",");
    const listPriceSavingText = vehicleDisplaySettings.listPriceSavingText;

    const onFinanceExampleButtonClick = async (
        event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement | MouseEvent> | undefined
    ) => {
        event?.preventDefault();

        const financeExample = await getFinanceExampleSidePanel({ vehicleId: vehicle.VehicleId });

        if (!financeExample) {
            openSidebar({
                title: "",
                content: {
                    component: () => <FinanceExampleSidePanelError vehicleId={vehicle.VehicleId} />,
                },
            });
        } else {
            openSidebar({
                title: financeExample.Title,
                content: {
                    component: () => <FinanceExampleSidePanel {...financeExample} />,
                    footerComponent: () => (
                        <FinanceExampleSidePanelFooter
                            vehicleAdvertUri={financeExample.VehicleAdvertUri}
                        />
                    ),
                },
            });
        }
    };

    return (
        <SmartLink href={vehicle.VehicleAdvertUrl || ""} target={linkTarget}>
            <a className={cx("link")}>
                <div className={cx("card")}>
                    <div className={cx("img")}>
                        {vehicleImageryOrdered != null && vehicleImageryOrdered?.length > 3 ? (
                            <picture>
                                {vehicleImageryOrdered.map((i) => {
                                    let mediaQueryString: string;
                                    switch (i.Size) {
                                        case "Small":
                                            mediaQueryString = "(min-width: 0px)";
                                            break;
                                        default:
                                            return;
                                    }
                                    return (
                                        <source
                                            media={mediaQueryString}
                                            srcSet={i.Uri}
                                            type={`image/${i.Format}`}
                                        />
                                    );
                                })}
                                <img
                                    src={vehicle?.FirstImage?.Uri || DEFAULT_IMAGE_URL}
                                    alt={vehicle?.FirstImage?.Description}
                                    loading="eager"
                                />
                            </picture>
                        ) : (
                            <Image
                                layout="fill"
                                objectFit="cover"
                                src={vehicle?.FirstImage?.Uri || DEFAULT_IMAGE_URL}
                                alt={vehicle?.FirstImage?.Description || ""}
                            />
                        )}
                        <ShortlistButton vehicleId={vehicle.StockBookNumber} isSaved={isSaved} />
                    </div>
                    <div className={cx("content")}>
                        {showListPriceSaving &&
                            listPriceSavingRegistrationYearCriteria.includes(
                                vehicle.RegPlateAgeIdentifier ?? ""
                            ) &&
                            savingsAgainstOriginalRetailPrice >= listPriceSavingThreshold && (
                                <div className={cx("listPriceSavingBanner")}>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                `Save ` +
                                                formatCurrency(
                                                    savingsAgainstOriginalRetailPrice,
                                                    0
                                                ) +
                                                ` `,
                                        }}
                                    />{" "}
                                    {listPriceSavingText}
                                </div>
                            )}
                        <h3 className={cx("heading")}>
                            <span className={cx("make")}>{vehicle.Make}</span>
                            <span className={cx("model")}>{vehicle.Model}</span>
                        </h3>
                        <hr />
                        <div className={cx("price")}>
                            <div className={cx("column")}>
                                {previousPrice && (
                                    <span className={cx("discount")}>
                                        {" "}
                                        Was{" "}
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: formatCurrency(previousPrice, 0),
                                            }}
                                        />
                                    </span>
                                )}
                                <span
                                    className={cx("price", {
                                        hasDiscount: !!previousPrice,
                                    })}
                                    dangerouslySetInnerHTML={{
                                        __html: formatCurrency(discountedPrice, 0),
                                    }}
                                />
                            </div>
                            {vehicle.LowestFinanceOption && (
                                <div className={cx("column")}>
                                    <button
                                        type="button"
                                        className={cx("financeExampleButton")}
                                        onClick={onFinanceExampleButtonClick}
                                    >
                                        Finance example
                                    </button>
                                    <span className={cx("perMonth")}>
                                        <strong
                                            dangerouslySetInnerHTML={{
                                                __html: formatCurrency(
                                                    vehicle.LowestFinanceOption,
                                                    0
                                                ),
                                            }}
                                        />{" "}
                                        per month
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                    {!!previousPrice && !!discountedPrice && isAdvertisedStock && (
                        <div className={cx("reducedPriceBadge")}>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: formatCurrency(previousPrice - discountedPrice, 0),
                                }}
                            />{" "}
                            off
                        </div>
                    )}
                </div>
                {hasCampaign &&
                    (!!campaignImageOverlay ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            className={cx("campaignImageOverlay")}
                            src={campaignImageOverlay}
                            alt="Promotional campaign image"
                        />
                    ) : (
                        <span
                            className={cx("campaignCustomOverlay")}
                            style={{
                                color: campaignCustomOverlay?.colour,
                                backgroundColor: campaignCustomOverlay?.backgroundColour,
                            }}
                        >
                            {campaignCustomOverlay?.copy}
                        </span>
                    ))}
            </a>
        </SmartLink>
    );
};

export default VehicleCardV2;
